import {InputLabel} from "@mui/material";
import React from "react";
import {DateUtils} from "../../../helpers/dateUtils";
import {ValidatedValue} from "../../../helpers/validatedValue";
import i18n from "../../../i18n/i18n";
import {TimetableModel} from "../../models/timetable";
import {TimetableService} from "../../services/timetableService";
import DropDown from "../../../common/components/shared/dropDown/dropDown";
import {Popup} from "../../../common/components/shared/popup/popup";
import {DropDownModel} from "../../../common/models/dropDown";
import "./copyWeekPopup.scss";
import {Api} from "../../../helpers/api";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import Error from "../../../common/components/shared/error/error";
import Loader from "../../../common/components/shared/loader/loader";

interface CopyWeekProps {
    timetableData: TimetableModel | undefined | null;
    openCopySelectedWeekPopup: boolean;
    yearToCopy: ValidatedValue<string>;
    weekToCopy: ValidatedValue<string>;
    yearCopyFrom: ValidatedValue<string>;
    weekCopyFrom: ValidatedValue<string>;
    onCancel: () => void;
    confirmCopyWeek: () => void;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    yearDropdownMap: DropDownModel[];
    weekDropDownMap: DropDownModel[];
    numberOfWeeks: number;
    isWaiting: boolean;
}

interface CopyWeekState {
    timetableDataToCopy: TimetableModel | undefined | null;
    timetableDataCopyFrom: TimetableModel | undefined | null;
    numberOfWeeks: number;
    displayError: boolean;
    loadingToCopy: boolean;
    loadingCopyFrom: boolean;
    responseTimetableDataCopy: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

class CopyWeekPopup extends React.Component<CopyWeekProps, CopyWeekState> {
    constructor(props: any) {
        super(props);

        this.state = {
            timetableDataToCopy: this.props.timetableData,
            timetableDataCopyFrom: null,
            numberOfWeeks: this.props.numberOfWeeks,
            displayError: false,
            loadingToCopy: false,
            loadingCopyFrom: false,
            responseTimetableDataCopy: undefined
        };
    }

    public render() {
        return <Popup
            open={this.props.openCopySelectedWeekPopup}
            className="copy-selected-week-popup"
            header={i18n.t("timetable.copyDataPopup.header")}
            content={this.renderContent()}
            actionText={i18n.t("timetable.copyDataPopup.actionText")}
            action={this.confirmCopyWeek.bind(this)}
            cancel={() => {
                this.setState({
                    loadingCopyFrom: false,
                    displayError: false,
                    timetableDataCopyFrom: null
                });
                this.props.onCancel();
            }}
            testId="copy-week-popup"
            httpError={
                <>
                    {!Api.isSuccess(this.props.response) && (
                        <HttpError
                            response={this.props.response}
                            cause={i18n.t("timetable.copyDataPopup.errorMessage")}
                        />
                    )}
                    {!Api.isSuccess(this.state.responseTimetableDataCopy) && (
                        <HttpError
                            response={this.state.responseTimetableDataCopy}
                            cause={i18n.t("timetable.copyDataPopup.errorSelectedWeekMessage")}
                        />
                    )}
                </>
            }
            disabledButton={this.state.displayError}
            isWaiting={this.props.isWaiting}
        />;
    }

    public renderContent() {
        return (
            <div>
                {this.renderWeekCopyFrom()}
                {this.renderWeekToCopy()}
            </div>
        );
    }

    private renderWeekToCopy() {
        return (
            <div className="popup-actions">
                <InputLabel>{i18n.t("timetable.copyDataPopup.toCopyLabel")}</InputLabel>
                <div className="week">
                    <DropDown
                        label={i18n.t("timetable.headers.filters.year")}
                        items={this.props.yearDropdownMap}
                        validatedValue={this.props.yearToCopy}
                        testId="filters-year-to-copy-popup"
                        onChange={this.onChangeCopyWeek.bind(this, "toCopy")}
                        withoutSort
                    />
                    <DropDown
                        label={i18n.t("timetable.headers.filters.week")}
                        items={this.props.weekDropDownMap}
                        validatedValue={this.props.weekToCopy}
                        testId="filters-week-to-copy-popup"
                        onChange={this.onChangeCopyWeek.bind(this, "toCopy")}
                        withoutSort
                    />
                </div>
                {this.renderSelectedDateRange(this.timetableDataToCopy, this.state.loadingToCopy, true)}
            </div>
        );
    }

    private renderWeekCopyFrom() {
        return (
            <div className="popup-actions">
                <InputLabel>{i18n.t("timetable.copyDataPopup.copyFromLabel")}</InputLabel>
                <div className="week">
                    <DropDown
                        label={i18n.t("timetable.headers.filters.year")}
                        items={this.props.yearDropdownMap}
                        validatedValue={this.props.yearCopyFrom}
                        testId="filters-year-copy-from-popup"
                        onChange={this.onChangeCopyWeek.bind(this, "copyFrom")}
                        withoutSort
                    />
                    <DropDown
                        label={i18n.t("timetable.headers.filters.week")}
                        items={this.props.weekDropDownMap}
                        validatedValue={this.props.weekCopyFrom}
                        testId="filters-week-copy-from-popup"
                        onChange={this.onChangeCopyWeek.bind(this, "copyFrom")}
                        withoutSort
                    />
                </div>
                {this.state.displayError && (
                    <Error message={i18n.t("timetable.copyDataPopup.copyWeekErrorMessage")}/>
                )}
                {this.renderSelectedDateRange(this.timetableDataCopyFrom, this.state.loadingCopyFrom, false)}
            </div>
        );
    }

    private renderSelectedDateRange(timetableData: TimetableModel | null | undefined, loading: boolean, isToCopy: boolean) {
        if (loading) {
            return <Loader />;
        } else {
            return (
                <div>
                    {timetableData?.startWeek && timetableData?.endWeek && (
                        <InputLabel data-testid={`${isToCopy ? "to-copy" : "copy-from"}-data-range`}>
                            {
                                DateUtils.getFormattedDate(timetableData?.startWeek)} - {DateUtils.getFormattedDate(timetableData?.endWeek)
                            }
                        </InputLabel>
                    )}
                </div>
            );
        }
    }

    private confirmCopyWeek() {
        if (!this.state.displayError) {
            this.props.confirmCopyWeek();
        }
    }

    private async onChangeCopyWeek(type: "toCopy" | "copyFrom") {
        const yearToCopy = Number.parseInt(this.props.yearToCopy.value);
        const weekToCopy = Number.parseInt(this.props.weekToCopy.value);
        const yearCopyFrom = Number.parseInt(this.props.yearCopyFrom.value);
        const weekCopyFrom = Number.parseInt(this.props.weekCopyFrom.value);

        if (yearCopyFrom && weekToCopy && yearToCopy && weekCopyFrom) {
            this.setState({displayError: ((yearCopyFrom === yearToCopy && weekCopyFrom >= weekToCopy) || (yearCopyFrom > yearToCopy))});
        }

        this.setState({responseTimetableDataCopy: undefined});
        if (type === "toCopy") {
            this.setState({loadingToCopy: true});
            const response = await TimetableService.getSelectedWeek(yearToCopy, weekToCopy);

            this.setState({
                timetableDataToCopy: response?.data || null,
                responseTimetableDataCopy: response,
                loadingToCopy: false
            });
        } else {
            if (yearCopyFrom && weekCopyFrom) {
                this.setState({loadingCopyFrom: true});
                const response = await TimetableService.getSelectedWeek(yearCopyFrom, weekCopyFrom);

                this.setState({
                    timetableDataCopyFrom: response?.data || null,
                    responseTimetableDataCopy: response,
                    numberOfWeeks: await this.getNumberOfWeeks(yearCopyFrom),
                    loadingCopyFrom: false
                });
            }
        }
    }

    private get timetableDataToCopy() {
        return this.state.timetableDataToCopy;
    }

    private get timetableDataCopyFrom() {
        return this.state.timetableDataCopyFrom;
    }

    private async getNumberOfWeeks(year: number): Promise<number> {
        const numberOfWeeksResponse = await TimetableService.getNumberOfWeeks(year);
        return numberOfWeeksResponse?.data?.numberOfWeeks || 0;
    }
}

export default CopyWeekPopup;
